import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { LayoutGrid, Loader2, Image, Trash2, Plus, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [templatesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/templates`),
          axios.get(`${API}/templates/categories`)
        ]);
        setTemplates(templatesRes.data);
        setCategories(['All', ...categoriesRes.data.categories]);
      } catch (error) {
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredTemplates = selectedCategory === 'All'
    ? templates
    : templates.filter(t => t.category === selectedCategory);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#09090b] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 font-['Space_Grotesk']">
            Meme Templates
          </h1>
          <p className="text-[#a1a1aa] text-lg max-w-2xl mx-auto">
            Browse our collection of popular meme templates. Click any template to start editing.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              className={selectedCategory === category
                ? 'bg-[#bef264] text-black hover:bg-[#a3e635]'
                : 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]'
              }
              data-testid={`filter-${category.toLowerCase()}`}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-[#bef264]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredTemplates.map((template, index) => (
              <Link
                key={template.id}
                to={`/editor?template=${template.slug_lower || template.slug?.toLowerCase() || template.id}`}
                className="template-card group aspect-square rounded-xl overflow-hidden border border-[#27272a] hover:border-[#bef264] transition-all animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`template-card-${template.id}`}
              >
                <div className="relative w-full h-full">
                  <img
                    src={template.url}
                    alt={template.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-white text-base font-semibold truncate">{template.name}</p>
                      <p className="text-[#a1a1aa] text-sm">{template.category}</p>
                    </div>
                  </div>
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-8 h-8 bg-[#bef264] rounded-lg flex items-center justify-center">
                      <Plus className="w-5 h-5 text-black" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {!loading && filteredTemplates.length === 0 && (
          <div className="empty-state py-20">
            <LayoutGrid className="w-16 h-16 text-[#3f3f46] mb-4" />
            <p className="text-[#a1a1aa] text-lg">No templates found in this category</p>
          </div>
        )}

        {/* CTA Section */}
        <div className="text-center mt-16 py-12 border-t border-[#27272a]">
          <h2 className="text-2xl font-bold text-white mb-4 font-['Space_Grotesk']">
            Don't see what you need?
          </h2>
          <p className="text-[#a1a1aa] mb-6">Upload your own image and start creating!</p>
          <Link to="/editor">
            <Button className="bg-[#bef264] text-black hover:bg-[#a3e635] font-medium">
              <Sparkles className="w-4 h-4 mr-2" />
              Upload Custom Image
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TemplatesPage;
